/**
 * Utility functions for search functionality
 */

/**
 * Extract document ID from search result
 * @param {Object} result - Search result object
 * @returns {string|null} - Document ID or null if not found
 */
export const extractDocumentId = (result) => {
  // Try different possible ID fields
  let documentId = result.id || 
         result.document_id || 
         result.object_id ||
         (result.metadata && result.metadata.id) ||
         null;
  
  // Clean up prefixed document IDs (e.g., "core_doc_23" -> "23")
  if (documentId && typeof documentId === 'string') {
    // Extract numeric ID from prefixed formats like "core_doc_23", "doc_456", etc.
    const numericMatch = documentId.match(/(\d+)$/);
    if (numericMatch) {
      documentId = numericMatch[1];
    }
  }
  
  // If no direct ID found, try to extract from admin_url
  if (!documentId && result.admin_url) {
    // Extract ID from URLs like /admin/core/intelligentdocument/123/change/ or /admin/core/document/456/change/
    const urlMatch = result.admin_url.match(/\/(?:intelligentdocument|document)\/(\d+)\//);
    if (urlMatch) {
      documentId = urlMatch[1];
    }
  }
  
  return documentId;
};

/**
 * Check if a search result represents a viewable document
 * @param {Object} result - Search result object
 * @returns {boolean} - True if result is a viewable document
 */
export const isViewableDocument = (result) => {
  const hasDocumentId = extractDocumentId(result) !== null;
  
  // Check various indicators that this is a document
  const isDocument = result.result_type === 'document' || 
                    result.source_module === 'core' ||
                    result.category === 'document' ||
                    result.category === 'intelligentdocument' ||
                    (result.admin_url && result.admin_url.includes('/intelligentdocument/')) ||
                    (result.admin_url && result.admin_url.includes('/document/')) ||
                    result.file_url ||
                    result.file_type;
  
  return isDocument && hasDocumentId;
};

/**
 * Get appropriate action for a search result click
 * @param {Object} result - Search result object
 * @returns {Object} - Action object with type and data
 */
export const getResultClickAction = (result) => {
  // PRIORITY 0: ERP entity with an in-app navigate_to URL (customer, SO, invoice, ...)
  const navigateTo = result?.metadata?.navigate_to;
  if (navigateTo) {
    return {
      type: 'navigate',
      url: navigateTo,
      result
    };
  }

  const documentId = extractDocumentId(result);
  const isViewable = isViewableDocument(result);

  // PRIORITY 1: If we can extract a document ID and it looks like a document, always view it
  if (documentId && isViewable) {
    return {
      type: 'document_view',
      documentId,
      result
    };
  }

  // PRIORITY 2: If we have a file URL, open it directly
  if (result.file_url) {
    return {
      type: 'file_url',
      url: result.file_url,
      result
    };
  }

  // PRIORITY 3: Only fall back to admin URL if no document viewing is possible
  if (result.admin_url) {
    return {
      type: 'admin_url',
      url: result.admin_url,
      result
    };
  }

  return {
    type: 'unknown',
    result
  };
};

/**
 * Build search context for navigation
 * @param {Object} params - Search parameters
 * @returns {Object} - Search context object
 */
export const buildSearchContext = ({ query, modules, filters, result = null }) => {
  return {
    fromSearch: true,
    query,
    modules,
    filters,
    searchResult: result,
    timestamp: Date.now()
  };
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const decimals = 1;
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${units[i]}`;
};

/**
 * Get file type category for display
 * @param {string} fileType - File extension or MIME type
 * @returns {string} - Category name
 */
export const getFileTypeCategory = (fileType) => {
  if (!fileType) return 'Unknown';
  
  const type = fileType.toLowerCase();
  
  // Images
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff'].includes(type)) {
    return 'Image';
  }
  
  // Documents
  if (['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'].includes(type)) {
    return 'Document';
  }
  
  // Spreadsheets
  if (['xls', 'xlsx', 'csv', 'ods'].includes(type)) {
    return 'Spreadsheet';
  }
  
  // Presentations
  if (['ppt', 'pptx', 'odp'].includes(type)) {
    return 'Presentation';
  }
  
  // Archives
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(type)) {
    return 'Archive';
  }
  
  // Code
  if (['js', 'html', 'css', 'py', 'java', 'cpp', 'c', 'php', 'json', 'xml'].includes(type)) {
    return 'Code';
  }
  
  // Media
  if (['mp4', 'avi', 'mov', 'wmv', 'mp3', 'wav', 'ogg'].includes(type)) {
    return 'Media';
  }
  
  return 'File';
};